import _ from "lodash";

type LicenseConfig = {
  type: string;
  priceId: string;
  cost: string;
};

export function generateLicenseTemplate(data: LicenseConfig[]): any {
  const licensingObj: any = {};

  // Helper functions
  const hasType = (type: string) => _.some(data, { type });
  const ensurePath = (path: string, value: any = {}) =>
    _.set(licensingObj, path, _.get(licensingObj, path) || value);

  // --- Recording Exclusive ---
  if (hasType("RecordingExclusive")) {
    ensurePath("recording.exclusive", {});
  }

  // --- Recording Non-Exclusive ---
  if (hasType("RecordingNonexclusiveMp3")) {
    ensurePath("recording.non-exclusive", { format: [], distribution: [] });
    licensingObj.recording["non-exclusive"].format.push("MP3");
  }
  if (hasType("RecordingNonexclusiveWav")) {
    ensurePath("recording.non-exclusive", { format: [], distribution: [] });
    licensingObj.recording["non-exclusive"].format.push("WAV");
  }
  if (hasType("RecordingNonexclusiveStemPlusWav")) {
    ensurePath("recording.non-exclusive", { format: [], distribution: [] });
    licensingObj.recording["non-exclusive"].format.push("Stem + WAV");
  }

  // Monetization
  if (hasType("RecordingNonexclusiveMp3WithMusicVideo")) {
    ensurePath("recording.non-exclusive", { format: [], distribution: [] });
    licensingObj.recording["non-exclusive"].canMonetize = true;
  }

  // --- Sync: Basic ---
  if (
    hasType("SyncBasicMp3") ||
    hasType("SyncBasicWav") ||
    hasType("SyncBasicStemPlusWav")
  ) {
    ensurePath("sync.basic.format", []);
    if (hasType("SyncBasicMp3")) licensingObj.sync.basic.format.push("MP3");
    if (hasType("SyncBasicWav")) licensingObj.sync.basic.format.push("WAV");
    if (hasType("SyncBasicStemPlusWav"))
      licensingObj.sync.basic.format.push("Stem + WAV");
  }

  // --- Sync: Pro ---
  if (hasType("SyncProWav") || hasType("SyncProStemPlusWav")) {
    ensurePath("sync.pro.format", []);
    if (hasType("SyncProWav")) licensingObj.sync.pro.format.push("WAV");
    if (hasType("SyncProStemPlusWav"))
      licensingObj.sync.pro.format.push("Stem + WAV");
  }

  // --- Sync: Enterprise ---
  if (hasType("SyncEnterprise")) {
    ensurePath("sync.enterprise.format", []);
  }

  // --- Soundkit ---
  // Check for both "SoundkitRoyaltyFree" and "SoundkitRoyaltyFreeLimited"
  if (hasType("SoundkitRoyaltyFree") || hasType("SoundkitRoyaltyFreeLimited")) {
    ensurePath("soundkit", {});
  }

  return licensingObj;
}

type PricingLicensing = {
  licensingObj: LicenseConfig[];
  licenseType: string;
  licenseRights: string;
  format: string;
  distribution: string;
  willMonetize: boolean;
};

export function getPricing({
  licensingObj,
  licenseType,
  licenseRights,
  format,
  distribution,
  willMonetize,
}: PricingLicensing) {
  const pricingTypeParts: string[] = [];

  // License type
  if (licenseType === "recording") pricingTypeParts.push("Recording");
  else if (licenseType === "sync") pricingTypeParts.push("Sync");
  else if (licenseType === "soundkit") {
    pricingTypeParts.push("SoundkitRoyaltyFree");
    // Add "Limited" suffix if distribution is limited
    if (distribution === "limited") {
      pricingTypeParts.push("Limited");
    }
  }

  // License rights
  const rightsMap: Record<string, string> = {
    "non-exclusive": "Nonexclusive",
    exclusive: "Exclusive",
    basic: "Basic",
    pro: "Pro",
    enterprise: "Enterprise",
  };
  if (rightsMap[licenseRights]) pricingTypeParts.push(rightsMap[licenseRights]);

  // Format
  const formatMap: Record<string, string> = {
    MP3: "Mp3",
    WAV: "Wav",
    "Stem + WAV": "StemPlusWav",
  };
  if (formatMap[format]) pricingTypeParts.push(formatMap[format]);

  // Distribution
  if (distribution === "unlimited") pricingTypeParts.push("WithDistribution");

  // Monetization
  if (willMonetize) {
    pricingTypeParts.push(
      distribution === "limited" ? "WithMusicVideo" : "MusicVideo",
    );
  }

  const pricingType = pricingTypeParts.join("");
  const match = licensingObj.find((e: LicenseConfig) => e.type === pricingType);

  return match || null;
}

export function splitLicenseType(type: string): string[] {
  // Split into capitalized segments, allowing lowercase and digits
  const parts = type.match(/[A-Z][a-z0-9]*/g) || [];

  const combined: string[] = [];

  for (let i = 0; i < parts.length; i++) {
    const current = parts[i];
    const next = parts[i + 1];
    const afterNext = parts[i + 2];

    // Combine special phrases
    if (current === "With" && next === "Distribution") {
      combined.push("WithDistribution");
      i++;
    } else if (current === "Music" && next === "Video") {
      combined.push("MusicVideo");
      i++;
    } else if (
      current === "With" &&
      next === "Music" &&
      afterNext === "Video"
    ) {
      combined.push("WithMusicVideo");
      i += 2;
    }
    // Combine "StemPlusWav" or "StemPlusMp3"
    else if (current === "Stem" && next === "Plus") {
      combined.push(`Stem`);
      i++;
    } else {
      combined.push(current);
    }
  }

  // Normalize acronyms to uppercase
  return combined.map((part) => {
    if (part.toUpperCase() === "MP3") return "MP3";
    if (part.toUpperCase() === "WAV") return "WAV";
    return part;
  });
}

export function getDefaultTemplate(licensingTemplate: any) {
  if (licensingTemplate["recording"]) {
    return {
      licenseType: "recording",
      licenseRights: "non-exclusive",
      format: licensingTemplate["recording"]["non-exclusive"].format[0],
      distribution: "limited",
    };
  }
  if (licensingTemplate["sync"]) {
    return {
      licenseType: "sync",
      licenseRights: "basic",
      format: licensingTemplate["sync"]["basic"].format[0],
    };
  }
  if (licensingTemplate["soundkit"]) {
    return {
      licenseType: "soundkit",
      licenseRights: "royalty-free",
      distribution: "limited",
    };
  }

  return;
}
